/**
 * RFC4180-ish CSV: quoted cells may contain newlines and the delimiter.
 * Keep in sync with functions/src/imports/csvParse.ts
 */

export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

export function detectCsvDelimiter(text: string): string {
  const first = stripBom(text)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .find((l) => l.length > 0) || '';
  const tab = (first.match(/\t/g) || []).length;
  const semi = (first.match(/;/g) || []).length;
  const comma = (first.match(/,/g) || []).length;
  if (tab > 0 && tab >= comma && tab >= semi) return '\t';
  if (semi > comma && semi > 0) return ';';
  return ',';
}

/** Parse the whole document into rows. Newlines inside "quotes" stay in the cell. */
export function parseCsvRecords(text: string, delimiter = ','): string[][] {
  const s = stripBom(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  const pushRow = () => {
    row.push(cell);
    cell = '';
    // Drop a fully blank row, but keep spaces and newlines inside a cell.
    if (row.some((c) => c.trim().length > 0)) {
      rows.push(row);
    }
    row = [];
  };

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      row.push(cell);
      cell = '';
      continue;
    }
    if (ch === '\n') {
      pushRow();
      continue;
    }
    cell += ch;
  }
  if (inQuotes || cell.length > 0 || row.length > 0) {
    pushRow();
  }
  return rows;
}

/** If a data row is only a URL in the title column, append it to the previous row's content. */
export function mergeUrlContinuationRows(
  records: string[][],
  titleIndex: number,
  contentIndex: number,
  dataStart: number
): string[][] {
  const looksUrl = (s: string) => /^https?:\/\//i.test(s.trim()) && /\./.test(s);
  const head = records.slice(0, dataStart);
  const merged: string[][] = [];
  for (const cols of records.slice(dataStart)) {
    const row = [...cols];
    const t = (row[titleIndex] || '').trim();
    const c = (row[contentIndex] || '').trim();
    if (merged.length > 0 && looksUrl(t) && !c) {
      const prev = merged[merged.length - 1];
      const prevC = (prev[contentIndex] || '').trim();
      prev[contentIndex] = prevC ? `${prevC}\n${t}` : t;
      continue;
    }
    merged.push(row);
  }
  return [...head, ...merged];
}
